import { z } from 'zod';
import { RoleTemplateSchema } from './role-template';
import { ErrorResponseSchema } from './errors';
// ============================================================================
// STORY TEMPLATE CONTENT CONSTANTS
// ============================================================================
export const DEFAULT_STORY_SPREAD_COUNT = 10;
export const DEDICATION_MAX_LENGTH = 500;
// ============================================================================
// AI PROVIDER SCHEMAS
// ============================================================================
export const AIProviderSchema = z.enum([
    'gemini',
    'openai',
    'anthropic',
    'fal',
]);
export const AIContentTypeSchema = z.enum([
    'CHARACTER_ATTRIBUTES',
    'CHARACTER_SHEET',
    'CHARACTER_PREVIEW',
    'STORY_TEMPLATE',
    'STORY_TEMPLATE_COVER',
    'STORY_GENERATION',
    'STORY_SPREAD_IMAGE',
    'SPREAD_TEXT_GENERATION',
    'IMAGE_UPSCALE',
]);
// ============================================================================
// STORY TEMPLATE CONTENT GENERATION SCHEMAS
// ============================================================================
// Schema for story spread - represents a spread or two pages in the story book
export const StorySpreadSchema = z.object({
    // Sequential number identifying this spread in the story
    spreadNum: z.number().int().min(1),
    // The narrative content for this story spread
    content: z.string().min(1, 'Content is required'),
    // AI prompt for generating the illustration for this spread
    imagePrompt: z.string().min(1, 'Image prompt is required'),
});
// Schema for story template content generation request
export const GenerateStoryTemplateContentRequestSchema = z.object({
    // The title of the story template to generate content for
    title: z.string().min(1, 'Title is required'),
    // Description providing context and direction for the story
    description: z.string().optional(),
    // Target age range to tailor content appropriately
    ageRange: z.string().min(1, 'Age range is required'),
    // Role templates defining the characters in the story
    roleTemplates: z
        .array(RoleTemplateSchema)
        .min(1, 'At least one role template is required'),
    // Themes to guide the narrative direction and tone
    storyThemes: z
        .array(z.string())
        .min(1, 'At least one story theme is required'),
    // Optional key events to include in the story narrative
    keyEvents: z.array(z.string()).optional(),
    // Optional setting details to establish the story environment
    settingDetails: z.string().optional(),
    // Number of story spreads to generate for the template
    spreadCount: z
        .number()
        .int()
        .min(1)
        .max(20, 'Spread count must be between 1 and 20'),
});
// Schema for story template content generation response
export const GenerateStoryTemplateContentResponseSchema = z.object({
    // Array of generated story spreads with content and image prompts
    spreads: z.array(StorySpreadSchema),
    // AI-generated prompt for creating the story cover image
    generatedCoverImagePrompt: z
        .string()
        .min(1, 'Cover image prompt is required'),
    // AI-generated tags for categorizing and discovering the story
    generatedTags: z
        .array(z.string())
        .min(3, 'At least 3 tags are required')
        .max(5, 'Maximum 5 tags allowed'),
    // AI-generated key events to enhance the story narrative
    generatedKeyEvents: z.array(z.string()).optional(),
    // AI-generated setting details to enrich the story world
    generatedSettingDetails: z.string().min(1, 'Setting details are required'),
});
// ============================================================================
// AI INVOCATION LOGGING SCHEMAS
// ============================================================================
// Complete AI invocation log schema
export const AIInvocationLogSchema = z.object({
    id: z.uuid(),
    user_id: z.uuid(),
    invocation_time: z.iso.datetime(),
    model_name: z.string(),
    ai_provider: AIProviderSchema,
    content_type: AIContentTypeSchema,
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
// Schema for creating new AI invocation logs
export const CreateAIInvocationLogSchema = z.object({
    user_id: z.uuid(),
    model_name: z.string(),
    ai_provider: AIProviderSchema,
    content_type: AIContentTypeSchema,
});
// ============================================================================
// GENERATED STORIES SCHEMAS
// ============================================================================
// Image generation status for cover and spreads
export const AIContentGenerationStatusSchema = z.enum([
    'NOT_STARTED',
    'COMPLETED',
    'ERROR',
]);
// Schema for story spread with image generation status
export const GeneratedStorySpreadSchema = StorySpreadSchema.extend({
    spread_text_generation_status: AIContentGenerationStatusSchema,
    spread_image_generation_status: AIContentGenerationStatusSchema,
});
// Schema for story spread with image URL
export const GeneratedStorySpreadWithImageUrlSchema = GeneratedStorySpreadSchema.extend({
    // Image URL for the spread
    imageUrl: z.string().optional(),
});
// Schema for character assignment to story template roles
export const CharacterAssignmentSchema = z.object({
    role_template_id: z.uuid(),
    character_id: z.uuid(),
});
// Schema for custom prompt data
export const CustomPromptDataSchema = z.object({
    generationMode: z.enum(['BASIC', 'CUSTOM']).default('BASIC'),
    customPrompt: z.string().optional(),
});
// Schema for generation status
export const StoryGenerationStatusSchema = z.enum([
    'PENDING',
    'PREVIEW_COMPLETED',
    'COMPLETED',
    'ERROR_TEXT',
    'ERROR_IMAGES',
    'ERROR_PREVIEW',
]);
export const DedicationSchema = z
    .string()
    .max(DEDICATION_MAX_LENGTH, `Dedication must be ${DEDICATION_MAX_LENGTH} characters or less`)
    .nullable();
// Schema for generated story
export const GeneratedStorySchema = z.object({
    id: z.uuid(),
    story_template_id: z.uuid(),
    custom_prompt_data: CustomPromptDataSchema,
    character_assignments: z.array(CharacterAssignmentSchema),
    title: z.string().min(1, 'Title is required'),
    cover_image_generation_status: AIContentGenerationStatusSchema.optional(),
    spreads: z.array(GeneratedStorySpreadSchema),
    dedication: DedicationSchema,
    generation_status: StoryGenerationStatusSchema,
    created_at: z.string(),
    updated_at: z.string(),
    deleted_at: z.string().nullable(),
    story_templates: z
        .object({
        id: z.uuid(),
        title: z.string(),
        description: z.string(),
        style_template_id: z.uuid(),
    })
        .optional(),
});
export const GeneratedStoryWithImageUrlsSchema = GeneratedStorySchema.extend({
    coverImageUrl: z.string().optional(),
    spreads: z.array(GeneratedStorySpreadWithImageUrlSchema),
});
// Schema for dedication form
export const DedicationFormSchema = z.object({
    dedication: DedicationSchema,
});
// Schema for creating a new generated story
export const CreateGeneratedStorySchema = z.object({
    story_template_id: z.uuid(),
    custom_prompt_data: CustomPromptDataSchema.optional(),
    title: z.string().min(1, 'Title is required'),
    dedication: DedicationSchema,
    character_assignments: z
        .array(CharacterAssignmentSchema)
        .min(1, 'At least one character assignment is required'),
});
// ============================================================================
// API RESPONSE SCHEMAS
// ============================================================================
// Success response schema for creating a generated story (POST)
export const CreateGeneratedStorySuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        story: GeneratedStoryWithImageUrlsSchema,
    }),
});
// Success response schema for getting a generated story (GET)
export const GetGeneratedStorySuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: GeneratedStoryWithImageUrlsSchema,
});
// Success response schema for multiple generated stories
export const GeneratedStoriesSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.array(GeneratedStoryWithImageUrlsSchema),
});
// Union type for creating a generated story API responses (POST)
export const CreateGeneratedStoryResponseSchema = z.discriminatedUnion('type', [
    CreateGeneratedStorySuccessResponseSchema,
    ErrorResponseSchema,
]);
// Union type for getting a generated story API responses (GET)
export const GetGeneratedStoryResponseSchema = z.discriminatedUnion('type', [
    GetGeneratedStorySuccessResponseSchema,
    ErrorResponseSchema,
]);
// Union type for multiple generated stories API responses
export const GeneratedStoriesResponseSchema = z.discriminatedUnion('type', [
    GeneratedStoriesSuccessResponseSchema,
    ErrorResponseSchema,
]);
// Schema for updating a generated story (PUT)
export const UpdateGeneratedStorySchema = z.object({
// No body required for generating rest of story - just triggers generation
});
// Success response schema for updating a generated story (PUT)
export const UpdateGeneratedStorySuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        story: GeneratedStoryWithImageUrlsSchema,
    }),
});
// Union type for updating a generated story API responses (PUT)
export const UpdateGeneratedStoryResponseSchema = z.discriminatedUnion('type', [
    UpdateGeneratedStorySuccessResponseSchema,
    ErrorResponseSchema,
]);
